import React, { useEffect, useState, useRef } from 'react';
import { Star, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { reviews as fallbackReviews } from '../../data/reviews';

export interface Review {
  city: string;
  mapLink: string;
  datePublished: string;
  reviewBody: string;
  reviewRating: {
    ratingValue: string;
    bestRating: string;
  };
  author: {
    name: string;
  };
}

export function ReviewsSection() {
  const [reviews, setReviews] = useState<Review[]>(fallbackReviews);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/api/reviews')
      .then(res => {
        const contentType = res.headers.get('content-type');
        if (res.ok && contentType && contentType.includes('application/json')) {
          return res.json().catch(() => []);
        }
        return [];
      })
      .then(data => {
        if (data && data.reviews && data.reviews.length > 0) {
          setReviews(data.reviews);
        }
      })
      .catch(err => console.error("Failed to load reviews, using fallback:", err));
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 350; // roughly the width of one card + gap
      const currentScroll = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (reviews.length === 0) return null;

  return (
    <section className="py-16 bg-brand-gray-light border-y border-gray-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div className="max-w-2xl">
            <div 
              className="inline-flex items-center gap-2 text-brand-gold font-bold tracking-widest uppercase text-xs mb-2 animate-[fadeInUp_0.6s_ease-out_both]"
            >
              <Star className="w-4 h-4 fill-current" />
              Kundenstimmen
            </div>
            <h2 
              className="text-2xl md:text-3xl font-heading font-bold text-brand-black animate-[fadeInUp_0.6s_ease-out_0.1s_both]"
            >
              Über 1.200 zufriedene Kunden
            </h2>
          </div>
          
          <div 
            className="flex gap-2 animate-[fadeIn_0.6s_ease-out_both]"
          >
            <button 
              onClick={() => scroll('left')}
              className="p-2 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-brand-red transition-colors shadow-sm"
              aria-label="Vorherige Bewertung"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={() => scroll('right')}
              className="p-2 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-brand-red transition-colors shadow-sm"
              aria-label="Nächste Bewertung"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="relative -mx-4 sm:mx-0">
          <div 
            ref={scrollContainerRef}
            className="flex overflow-x-auto gap-4 md:gap-6 px-4 sm:px-0 pb-4 snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {/* CSS to hide scrollbar for webkit */}
            <style dangerouslySetInnerHTML={{__html: `
              .scrollbar-hide::-webkit-scrollbar {
                display: none;
              }
            `}} />
            
            {reviews.map((review, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm shrink-0 w-[300px] md:w-[350px] snap-center flex flex-col relative animate-[fadeInUp_0.6s_ease-out_both]"
                style={{ animationDelay: `${(index % 4) * 0.05}s` }}
              >
                <div className="flex text-brand-gold mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                
                <div className="mb-4">
                  <div className="font-bold text-sm text-brand-black">{review.author.name}</div>
                  <a 
                    href={review.mapLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-brand-red text-xs flex items-center gap-1 hover:underline mt-0.5"
                  >
                    <MapPin className="w-3 h-3" />
                    {review.city}
                  </a>
                </div>
                <div className="text-[10px] text-gray-400">
                  {new Date(review.datePublished).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}
                </div>
                
                <blockquote className="text-gray-600 font-light text-sm italic mb-4 leading-relaxed">
                  "{review.reviewBody}"
                </blockquote>
                
                <div className="mt-auto border-t border-gray-100 pt-3 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <img referrerPolicy="no-referrer" src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" className="w-4 h-4" />
                    <a 
                      href={'https://konig-rohrreinigung-klempner-notdienst.de'} 
                      target="_self"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-gray-500 uppercase tracking-wider hover:text-brand-red transition-colors"
                    >
                      Auf Google ansehen
                    </a>
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {new Date(review.datePublished).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
